import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AnimatedSection, AnimatedHeading, StaggerContainer, StaggerItem } from '../components/common/AnimatedSection';
import { ArrowRight, FlaskConical, Beaker, Activity, FileText, Database, TestTube,  Leaf, Zap, Target, TrendingUp, 
  Shield, Atom, AlertTriangle,
  CheckCircle2, Gauge, BarChart3, Microscope,
  Thermometer, Scale, Settings, Clock, Layers } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};
// Complete 35 Lab Services from PDF
const labServices = [
  { id: 1, name: "On-site and Off-site Lab Establishments", category: "Setup" },
  { id: 2, name: "pH", category: "Basic" },
  { id: 3, name: "TS (Total Solids)", category: "Basic" },
  { id: 4, name: "VS (Volatile Solids)", category: "Basic" },
  { id: 5, name: "COD (Chemical Oxygen Demand)", category: "Basic" },
  { id: 6, name: "BOD (Biochemical Oxygen Demand)", category: "Basic" },
  { id: 7, name: "Moisture content", category: "Basic" },
  { id: 8, name: "Ash Content", category: "Basic" },
  { id: 9, name: "TC (Total Carbon)", category: "Nutrient" },
  { id: 10, name: "TOC (Total Organic Carbon)", category: "Nutrient" },
  { id: 11, name: "TKN (Total Nitrogen)", category: "Nutrient" },
  { id: 12, name: "C/N ratio", category: "Nutrient" },
  { id: 13, name: "Alk (Alkalinity)", category: "Process" },
  { id: 14, name: "VFA (Volatile Fatty Acids)", category: "Process" },
  { id: 15, name: "VFA/Alkalinity Ratio", category: "Process" },
  { id: 16, name: "Ripley's Ratio", category: "Process" },
  { id: 17, name: "TAN- Total Ammoniacal Nitrogen", category: "Nutrient" },
  { id: 18, name: "CHNS", category: "Advanced" },
  { id: 19, name: "K (Potassium)", category: "Nutrient" },
  { id: 20, name: "P (Phosphate)", category: "Nutrient" },
  { id: 21, name: "S (Sulphates)", category: "Nutrient" },
  { id: 22, name: "NDF (neutral detergent fibre)", category: "Fibre" },
  { id: 23, name: "ADF (acid detergent fibre)", category: "Fibre" },
  { id: 24, name: "ADL (acid detergent lignin) = lignin", category: "Fibre" },
  { id: 25, name: "Hemicellulose", category: "Fibre" },
  { id: 26, name: "Cellulose", category: "Fibre" },
  { id: 27, name: "BGP-Biogas Potential Test", category: "Biogas" },
  { id: 28, name: "BMP-Biochemical Methane Test", category: "Biogas" },
  { id: 29, name: "SMA-Specific Methanogenic Activity Test", category: "Biogas" },
  { id: 30, name: "Biogas composition (GC)", category: "Biogas" },
  { id: 31, name: "Biogas composition (Hand Held Analyzer)", category: "Biogas" },
  { id: 32, name: "Substrate Profile", category: "Profile" },
  { id: 33, name: "LOFM Profile", category: "Profile" },
  { id: 34, name: "SOFM profile", category: "Profile" },
  { id: 35, name: "Soil Profile for Napier cultivation", category: "Profile" }
];
const processParameters = [
  { param: "Feedstock quantity", category: "Key Process Parameters" },
  { param: "Composition", category: "Key Process Parameters" },
  { param: "Biodegradability", category: "Key Process Parameters" },
  { param: "TS/VS", category: "Key Process Parameters" },
  { param: "BMP", category: "Key Process Parameters" },
  { param: "NH₄−N", category: "Key Process Parameters" },
  { param: "OLR", category: "Key Process Parameters" },
  { param: "Temperature", category: "Key Process Parameters" },
  { param: "pH", category: "Key Process Parameters" },
  { param: "Mixing regime", category: "Key Process Parameters" },
  { param: "HRT and SRT", category: "Key Process Parameters" }
];
const processIndicators = [
  { param: "Total biogas yield", category: "Key Process Indicators" },
  { param: "Specific methane yield", category: "Key Process Indicators" },
  { param: "Biogas composition (CH₄, CO₂, H₂, H₂S, NH₃)", category: "Key Process Indicators" },
  { param: "VFA profile", category: "Key Process Indicators" },
  { param: "FOS/TAC", category: "Key Process Indicators" },
  { param: "ORP", category: "Key Process Indicators" }
];
const monitoringStrategy = [
  { frequency: "Daily", parameters: "Temperature, pH, mixing, biogas composition" },
  { frequency: "Twice weekly", parameters: "TS, VS, OLR" },
  { frequency: "Bi-monthly", parameters: "VFAs and FOS/TAC" }
];

export const BiogasLabPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="biogas-lab-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Lab Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              RENVITEK's Biogas Lab
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto tracking-wider">
              Empowering the Future of Biogas – With RENVITEK Lab Services. <br></br>
              Whether establishing a new biogas plant or managing multiple operational units, RENVITEK’S LAB supports clients in achieving sustainable energy goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: FlaskConical,
                title: 'Analytical Services',
                description: 'Precise, data-driven analysis of AD and RNG feedstocks, digestates, and process side-streams.'
              },
              {
                icon: Beaker,
                title: 'BMP & SMA Testing',
                description: 'Standardized laboratory-scale anaerobic batch assays for methane potential evaluation.'
              },
              {
                icon: Activity,
                title: 'Process Optimization',
                description: 'Lab-scale validation of optimization strategies for low-risk, cost-effective improvements.'
              },
              {
                icon: FileText,
                title: 'Insightful Reports',
                description: 'Clear, insight-focused analytical reports for confident process optimization decisions.'
              },
              {
                icon: Database,
                title: 'RENVITEK Database',
                description: 'Real-time access to biogas production curves during ongoing BMP tests.'
              },
              {
                icon: TestTube,
                title: 'Feedstock Characterization',
                description: 'Comprehensive analysis of biomass samples for AD and RNG applications.'
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <service.icon className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-3">{service.title}</h3>
                <p className="text-slate-600 dark:text-white/70 tracking-wider">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Lab Services List - All 35 */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-royal/10 text-royal text-sm font-body font-medium mb-4">
              Complete Lab Services
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-royal-dark mb-4">
              35+ Analytical Testing Services
            </h2>
            <p className="font-body text-royal-dark/70 max-w-2xl mx-auto tracking-wider">
              Comprehensive analytical testing for biogas and AD processes
            </p>
          </AnimatedSection>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-8 bg-transparent">
              {["All", "Basic", "Nutrient", "Process", "Fibre", "Biogas", "Profile", "Advanced", "Setup"].map((cat) => (
                <TabsTrigger 
                  key={cat} 
                  value={cat.toLowerCase()}
                  className="px-4 py-2 rounded-sm font-body text-sm data-[state=active]:bg-royal data-[state=active]:text-amber-400 border border-royal/20"
                  data-testid={`tab-${cat.toLowerCase()}`}
                >
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>

            {["all", "basic", "nutrient", "process", "fibre", "biogas", "profile", "advanced", "setup"].map((category) => (
              <TabsContent key={category} value={category}>
                <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {labServices
                    .filter(s => category === "all" || s.category.toLowerCase() === category)
                    .map((service, index) => (
                      <StaggerItem key={service.id}>
                        <Card className="h-full bg-white border border-royal/10 hover:border-royal/30 shadow-sm hover:shadow-royal transition-all duration-300">
                          <CardContent className="p-4 flex items-start gap-3">
                            <div className="w-8 h-8 rounded-sm bg-royal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-royal font-mono text-sm font-bold dark:text-black/80">{service.id}</span>
                            </div>
                            <div>
                              <p className="font-body font-medium text-royal-dark dark:text-black/80 tracking-wider">{service.name}</p>
                              <Badge variant="outline" className="mt-2 text-xs border-royal/20 text-royal dark:text-black/80">
                                {service.category}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      </StaggerItem>
                    ))}
                </StaggerContainer>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
      {/* Analytical & Bioprocess Development */}
      <section className="py-12 md:py-10 md:py-32 bg-[#F0FDF4]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection direction="left">
              <span className="inline-block px-4 py-1.5 rounded-full bg-royal/10 text-royal text-sm font-body font-medium mb-4">
                Analytical Excellence
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-royal-dark mb-6">
                Analytical and Bioprocess Development for the Anaerobic Digestion Sector
              </h2>
              <p className="text-slate-600 dark:text-white/70 leading-relaxed tracking-wide tracking-wider">
                RENVITEK specializes in analytical and bioprocess development for the bioeconomy, delivering precise, data-driven analysis of AD and RNG feedstocks, digestates, and process side-streams. With strong expertise in anaerobic digestion biology and system dynamics, we help biogas and CBG producers across India maximize efficiency, stability, and yield, driving a circular and sustainable energy future.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-sm bg-gold flex items-center justify-center flex-shrink-0">
                    <Database className="text-royal-dark" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-royal-dark">Insightful Data, Powerful Decisions</h4>
                    <p className="font-body text-sm text-royal-dark/70 tracking-wider">Our advanced laboratories and skilled technical team execute comprehensive projects and deliver clear, insight-focused analytical reports that enable confident, timely process optimization decisions.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-sm bg-gold flex items-center justify-center flex-shrink-0">
                    <Activity className="text-royal-dark" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-royal-dark">Real-Time Results – The RENVITEK Database</h4>
                    <p className="font-body text-sm text-royal-dark/70 tracking-wide tracking-wider">Clients gain real-time access to biogas production curves during ongoing BMP tests through the RENVITEK Database, ensuring continuous visibility of data, insights, and progress without waiting for final reports.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection direction="right">
              <Card className="text-slate-600 dark:text-white/70">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-amber-400 mb-3">
                    <Leaf className="text-gold" size={28} />
                    Empowering the Future of Biogas
                  </h3>
                  <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed grid grid-cols-2 gap-4 tracking-wider">Your Trusted Partner in Sustainable Energy Solutions</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { title: "Analytical Expertise", items: ["Feedstock Analysis", "Digestate Testing", "Process Optimization"] },
                      { title: "Real-Time Data Access", items: ["RENVITEK Database", "Live Biogas Monitoring", "Instant Insights"] },
                      { title: "Insightful Reports", items: ["Advanced Labs", "In-Depth Reports"] },
                      { title: "Tailored Solutions", items: ["Custom Analysis", "Regional Adaptation"] }
                    ].map((block, i) => (
                      <div key={i} className="bg-white/10 rounded-sm p-4">
                        <h4 className="font-body font-semibold text-gold text-sm mb-2">{block.title}</h4>
                        <ul className="space-y-1">
                          {block.items.map((item, j) => (
                            <li key={j} className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
                              <div className="w-1 h-1 rounded-full bg-gold" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >          
          <div className="mb-8">
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Your AD Experts – Trusted Nationwide
            </h3>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              Serving biogas and CBG clients across India, RENVITEK provides customized analytical, design, and optimization solutions tailored to regional conditions and feedstock variability. Customs exemptions for research samples ensure fast turnaround times nationwide.
                        </p>
                      </div>
            <div className="mb-8">
                        <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                          Our Strength – Knowledge, Passion & Precision
                        </h3>
                        <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
                          Our multidisciplinary team combines deep expertise in process formulation, optimization, and commercial evaluation of anaerobic digestion systems, delivering scientific rigor and practical excellence in every project.

            </p>
          </div>
          <div className="mb-8">
                        <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                          Anaerobic Digestion (AD): Background & Benefits
                        </h3>
                        <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
                          Anaerobic digestion is a sustainable waste-to-energy technology that converts organic waste into methane-rich biogas (CH ) and nutrient-rich digestate, supporting renewable energy generation₄ and circular bioeconomy goals. Rising global regulations have accelerated the shift from landfilling to biological onversion systems like AD. India’s early digester (1897) reflects the technology’s long-standing relevance, now applied worldwide—from farm-scale manure systems to large MSW and industrial plants. AD’s scalability, flexibility, and tolerance to heterogeneous feedstocks make it ideal for low-carbon energy and waste valorisation.Environmental & Operational AdvantagesCompared to aerobic treatment and andfilling, AD offers higher energy recovery, lower sludge generation, higher OLR tolerance, nd significant COD reduction, while stabilizing odours and reducing pathogens (highest in thermophilic systems; mesophilic requires longer retention).

            </p>
          </div>
          
        </motion.div>
        </div>
      </section>
      {/* key concept */}
      <section className="py-12 md:py-10 bg-white-50 dark:bg-emerald-900/30">
      <div className="w-full px-6 lg:px-16 text-white">

        {/* ================= KEY MESSAGE ================= */}
         <motion.h3
          className="text-3xl font-bold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          RENVITEK Contribution
        </motion.h3>
        <motion.p
          className="text-white/80 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            RENVITEK integrates feedstock characterization, process modelling, OLR optimization, and monitoring to deliver stable operation, higher biomethane yields, regulatory compliance, and improved ROI—enabling effective, ustainable AD systems.</p>
        </motion.p>
        <motion.h3
          className="text-3xl font-bold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Understanding the Biology of Anaerobic Digestion — The RENVITEK Approach
        </motion.h3>
        <motion.p
          className="text-white/80 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider ">
            Anaerobic digestion is a fully microbial-driven process in which the stability and performance of the digester depend on a balanced interaction of microbial communities converting organic feedstock into biogas. This iological balance is governed by reactor operating conditions and feedstock nutrient composition.</p>
        </motion.p>
        <motion.h3
          className="text-3xl font-bold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Key process indicators
        </motion.h3>
        <motion.p
          className="text-white/80 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            Key process indicators—including FOS/TAC ratio, VFA profiles, ammonia, and hydrogen sulphide—provide critical insight into digester health and stability. Feedstock-related challenges, such as incompatible substrates, high organic loading rates, or elemental imbalances (e.g., sulphur), commonly disrupt microbial activity and reduce methane yield.</p><br></br>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK integrates advanced analytical tools and RBHM (RENVITEK Biodigester Health 
Monitoring) to correlate biological parameters with biogas production trends. Our diagnostics 
include Specific Methanogenic Activity (SMA) tests, Anaerobic Toxicity Assays (ATA), and 
bioprocess evaluations to identify stressed or inhibited microbial groups.</p><br></br>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Based on these insights, RENVITEK designs targeted bio-optimization strategies—adjusting 
OLR, refining feedstock ratios, or applying nutrient supplementation—to restore microbial 
balance and enhance productivity. Our specialists also identify early warning indicators of 
biological stress, enabling timely corrective actions before major failures occur.</p><br></br>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">In essence, RENVITEK converts complex biological and process data into actionable 
intelligence for stable, high-yield, and sustainable biogas operations. Biochemical process 
Consultation Services at RENVITEK
</p><br></br>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK’s biological consultation and bioprocess monitoring services are led by Dr. Srinivas 
Jukuri, Founder & Managing Director, with a Ph.D. in anaerobic digestion process optimization 
and early warning indicators and over a decade of experience developing RBHM (RENVITEK 
Biodigester Health Monitoring) technologies. He provides expert biochemical evaluation of AD 
systems by correlating operational data with digester performance to identify root causes of 
instability and recommend precise interventions to stabilize and enhance biogas yield. He is 
supported by RENVITEK’s Biogas–CBG technical team, which conducts targeted analyses of 
digesters and feedstocks to deliver customized recommendations on feedstock mix ratios, 
retention time, inhibitor control, trace element/additive supplementation, and overall process 
efficiency and stability.</p><br></br>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK defines optimal macro- and microelement ranges with upper and lower thresholds, 
prescribing custom nutrient formulations for deficiencies and corrective strategies for excesses—
ensuring targeted recovery and long-term biological stability.</p><br></br>
            <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">How it works: Services are tailored to client needs through review of historical and third-party 
data, in-house analysis of digester/feedstock samples (e.g., FOS/TAC, VFAs, KPIs), and 
advanced assays for microbial activity or inhibition. Quarterly biological audits, audits after 
feedstock changes, or post-performance drops/crashes are recommended to prevent recurrence.
Ongoing Monitoring & AMCs: RENVITEK provides continuous bioprocess monitoring and 
optimization (including AMCs) covering OLR and feedstock loading, mixing/recirculation, 
biogas yield and composition, VFAs and alkalinity. Advanced diagnostics extend beyond 
conventional ratios to include acetic: propionic ratios, trace hydrogen, H S, and NH , presented
₂
in a Green–Yellow–Red traffic-light framework for early action.</p><br></br>
<p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">A custom nutrient design tool dynamically optimizes major/minor element supplementation for 
mono- and co-digestion based on feedstock chemistry, enabling smooth feedstock transitions 
without compromising stability.</p>
        </motion.p>
         </div>
    </section>
    
    {/* Process Monitoring Section */}
      {/* <section className="py-20 md:py-32 bg-royal">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Why Process Monitoring in Biogas–CBG Plants Is <span className="text-gold">Critical</span>
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              Sustained methane production and digester stability require continuous monitoring of operational parameters and biological indicators to assess digester health, microbial activity, and substrate-to-methane conversion efficiency.
            </p>
          </AnimatedSection>

          <div className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <AnimatedSection delay={0.1}>
              <Card className="text-2xl font-semibold text-amber-400 mb-6">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-amber-400 mb-6">
                    <Settings size={20} />
                    Key Process Parameters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-body text-sm text-white/90">
                    {processParameters.map((p, i) => (
                      <li key={i} className="text-sm text-slate-600 dark:text-white/70 flex items-start gap-2 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                        {p.param}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <Card className="text-2xl font-semibold text-amber-400 mb-6">
                <CardHeader>
                  <CardTitle className="font-heading text-gold flex items-center gap-2">
                    <BarChart3 size={20} />
                    Key Process Indicators
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-body text-sm text-white/90">
                    {processIndicators.map((p, i) => (
                      <li key={i} className="text-sm text-slate-600 dark:text-white/70 flex items-start gap-2 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                        {p.param}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <Card className="text-2xl font-semibold text-amber-400 mb-6">
                <CardHeader>
                  <CardTitle className="font-heading text-gold flex items-center gap-2">
                    <Clock size={20} />
                    Monitoring Strategy
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4 font-body text-sm">
                    {monitoringStrategy.map((m, i) => (
                      <li key={i}>
                        <span className="text-sm text-slate-600 dark:text-white/70 flex items-start gap-2 text-gold font-semibold">{m.frequency}:</span>
                        <p className="dark:text-white/90 mt-1">{m.parameters}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>

          <AnimatedSection>
            <Card className="py-2 md:py-4 bg-[#F0FDF4]">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-royal flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="text-gold" size={40} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold mb-2">During Process Changes</h3>
                    <p className="font-body">
                      Gradual adjustments to feedstock, OLR, or HRT/SRT are critical. Rapid changes can destabilize the microbial community and lead to process failure.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section> */}
      
      {/* Key Tests Explained */}
      {/* <section className="py-20 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
              Key Tests & Formulas
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
        
            <motion.div
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-4">
                Biomethane Potential (BMP)
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-4">
                The maximum achievable methane yield from an organic substrate under optimal anaerobic conditions.
              </p>
              <div className="formula">
                <p className="text-sm mb-2">CO₂ absorption reaction:</p>
                <p className="font-mono text-emerald-700 dark:text-emerald-400">
                  CO<sub>2</sub> + 2NaOH → Na<sub>2</sub>CO<sub>3</sub> + H<sub>2</sub>O
                </p>
              </div>
              <p className="text-sm text-slate-500 dark:text-white/50">
                Results expressed as <span className="font-mono">mL CH₄ per g VS added</span>
              </p>
            </motion.div>

            <motion.div
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-4">
                Food-to-Microorganism (F/M) Ratio
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-4">
                Key operational parameter governing stability and performance of anaerobic digesters.
              </p>
              <div className="formula">
                <p className="font-mono text-emerald-700 dark:text-emerald-400 text-lg">
                  F/M = (VS<sub>feed</sub> × Q) / (V<sub>digester</sub> × X)
                </p>
              </div>
              <ul className="text-sm text-slate-600 dark:text-white/70 space-y-1 mt-4">
                <li><span className="font-mono">VS<sub>feed</sub></span> = Volatile solids in feedstock (g/L)</li>
                <li><span className="font-mono">Q</span> = Feed flow rate (L/day)</li>
                <li><span className="font-mono">V<sub>digester</sub></span> = Working volume (L)</li>
                <li><span className="font-mono">X</span> = VSS concentration (g/L)</li>
              </ul>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-4">
                Optimal range: 0.3-0.5 g VS/g VSS-day
              </p>
            </motion.div>

            
            <motion.div
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-4">
                Specific Methanogenic Activity (SMA)
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-4">
                Measures the maximum rate of methane production by active microbial biomass.
              </p>
              <div className="formula">
                <p className="font-mono text-emerald-700 dark:text-emerald-400">
                  SMA = mL CH₄ / (g VSS × day)
                </p>
              </div>
              <p className="text-sm text-slate-500 dark:text-white/50 mt-4">
                Used for microbial activity profiling and bioaugmentation protocol development.
              </p>
            </motion.div>

             VFA/Alkalinity
            <motion.div
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-4">
                VFA/Alkalinity Ratio (FOS/TAC)
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-4">
                Critical indicator for assessing system's buffering strength and preventing acidification.
              </p>
              <div className="formula">
                <p className="font-mono text-emerald-700 dark:text-emerald-400">
                  Stable digester: VFA/BA ≤ 0.3
                </p>
              </div>
              <p className="text-sm text-slate-500 dark:text-white/50 mt-4">
                Recommended bicarbonate alkalinity: 1000–5000 mg CaCO₃/L to maintain pH &gt; 7
              </p>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* During Process Changes */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">
  During Process Changes - Explain in this section
</h2>
          <div className="mb-8">
            
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Gradual adjustments to feedstock, OLR, or HRT/SRT are essential to allow microbial 
acclimation. Sudden changes disrupt acidogenic–methanogenic balance, causing VFA 
accumulation, pH decline, and methane loss. Close monitoring of VFAs, alkalinity, pH, and gas 
composition enables early intervention, prevents inhibition, and ensures long-term digester 
stability.</p>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Early Warning Indicators in Anaerobic Digestion — RENVITEK Approach
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Despite AD being a mature technology, process instability remains common; hence continuous 
monitoring is critical. Core indicators include biogas yield/composition, pH, TS, VS, alkalinity, 
VFAs, and Ripley’s ratio. Among these, VFAs, alkalinity, and pH are tightly linked and provide 
rapid signals of imbalance.
</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">VFA accumulation reflects a mismatch between acidogenesis and methanogenesis. While acetate 
is usually tolerated, buildup of propionate and higher VFAs (butyrate, valerates) indicates 
inhibition; the propionic:acetic ratio is a robust marker because propionate degrades slowly after 
overloading.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Although GC/HPLC/GC–MS accurately profile VFAs, they are costly. Total VFA (TVFA) via 
titration offers a practical alternative, but lacks universal thresholds and can be confounded by 
TAN in unstable systems.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Alkalinity is a more reliable control parameter than pH, representing buffering capacity (mainly 
HCO , effective at pH 5.3–7.3). Recommended bicarbonate alkalinity: 1000–5000 mg CaCO /L
₃
to maintain pH >7. A stable digester typically shows VFA/BA ≤0.3. The IA/PA (Ripley’s) ratio 
is widely accepted for operational stability, despite debates on titration endpoints.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Building on extensive five-point titration studies across start-up to overload phases, Dr. Srinivas 
Jukuri developed RBHM (RENVITEK Bioprocess Health Monitoring)—a cost-effective, real
time system delivering reliable early warnings of imbalance versus GC/HPLC, supporting long
term stability across OLR, VFAs, alkalinity, and biogas production.</p>
          </div>
<div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Continuous Digestion Studies — RENVITEK
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK conducted long-term continuous digestion studies across commercial biogas plants 
of varying capacities using ~45–50 substrates (cow dung, MSW, food waste, fruit pulp, kitchen 
and market wastes). The aim was to define maximum sustainable OLR, optimum substrate 
blends, and minimum OLR for stable operation during feedstock shortages.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Operational data were integrated with microbial activity assay SMA to understand digester 
microbiology and guide adaptation to variable or new feedstocks. Analysis established clear 
links between feedstock characteristics, OLR, and key indicators (VFAs, alkalinity, biogas yield, 
gas composition), enabling definition of stable operating boundaries and impacts of under-/over
loading.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">This framework supports adaptive process control, ensuring long-term stability, higher biogas 
productivity, and sustained digester health under changing feedstock conditions.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Optimum Plant Operation and Nutrient Dosage
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">The RENVITEK delivers advanced bioprocess monitoring and performance optimization 
solutions for a wide range of biogas and compressed biogas (CBG) facilities. Through 
comprehensive analysis of plant operational data, RENVITEK has developed a feedstock
specific nutrient dosage formulation system designed for precise major and trace element 
supplementation in both mono-digestion and co-digestion systems.</p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Accurately determine optimal nutrient and trace element requirements based on the chemical 
composition of feedstocks.</li>
              <li>Seamlessly adjust nutrient formulations during transitions between different feedstocks.</li>
              <li>Incorporate new substrates into existing co-digestion regimes without compromising 
microbial balance or reactor performance.</li>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">By integrating the nutrient dosage formulation into routine plant operations, RENVITEK ensures:</p>
<ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxe"></ul>
              <li>Optimized nutrient balance and sustained microbial activity.</li>
              <li>Enhanced process efficiency and reactor stability.</li>
              <li>Higher methane yields and reduced risk of process inhibition.</li>
            </ul>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">This adaptive nutrient management approach provides a robust framework for long-term digester 
stability and consistent plant performance, even under variable feedstock supply conditions.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Biomethane Potential (BMP) Analysis
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">The Biomethane Potential (BMP) represents the maximum achievable methane yield from an 
organic substrate under optimal anaerobic conditions. It serves as a critical parameter for 
evaluating the biodegradability and bioenergy recovery potential of substrates intended for 
biogas or compressed biogas (CBG) production.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">At RENVITEK, the BMP is determined through standardized laboratory-scale anaerobic batch 
assays. The test involves mixing a defined quantity of the organic substrate with a well
acclimated anaerobic inoculum in airtight reactors. These reactors are incubated under controlled 
mesophilic conditions (35 ± 2 °C) with continuous or intermittent mixing to ensure 
homogeneous distribution of substrates and microbial populations.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">The experiment continues until the daily biogas generation falls below 1% of the total 
cumulative gas yield, indicating digestion completion. The produced biogas is collected and 
analysed for methane (CH<sub>4</sub> ) and carbon dioxide (CO<sub>2</sub>) concentrations using calibrated gas analysers.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">To ensure accurate methane quantification, carbon dioxide is absorbed using a sodium hydroxide 
(NaOH) solution via the reaction:<br></br><p className="text-lg text-center">
  CO<sub>2</sub> + 2NaOH → Na<sub>2</sub>CO<sub>3</sub> + H<sub>2</sub>O
</p><br></br>This process isolates the methane fraction, allowing precise measurement of biomethane yield. 
The results are expressed as mL CH per g VS added, providing a reliable metric for evaluating
the biomethane generation potential of the feedstocks.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">All BMP assays at RENVITEK are conducted in triplicate to ensure reproducibility and 
statistical accuracy, with the mean and standard deviation reported. Our methodologies align 
with internationally recognized standards such as:<ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>VDI 4630:2016 (Germany)</li>
              <li>Holliger et al., 2016 (IWA Guidelines)</li>
              <li>Drosg et al., 2013 (IEA Bioenergy Task 37 Manual)</li>
            </ul>Through this comprehensive approach, RENVITEK provides accurate, reproducible, and 
actionable data for feedstock characterization, digester performance prediction, and CBG project 
optimization.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Process Optimisation
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Effective anaerobic digestion (AD) optimisation requires coordinated control of feedstock 
properties, co-digestion ratios, OLR, HRT/SRT, viscosity, and nutrient/trace metal supply. Lab
scale validation of optimisation strategies enables low-risk, cost-effective improvements before 
full-scale implementation.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Poor control of these parameters reduces biogas yield, causes digestate buildup, and can trigger 
digester instability, increasing downtime and revenue loss. Establishing clear control limits for 
key indicators and optimising critical variables ensures stable, predictable, high-yield operation.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK designs, tests, and validates these optimisation strategies at laboratory scale, 
delivering reliable performance and maximised output prior to commercial deployment.</p>
          </div>
        </motion.div>
        </div>
      </section>

      {/* Co-Digestion, Methane Selectivity & Nutrient Optimization — RENVITEK */}
      <section className="py-12 md:py-10 bg-white-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">
  Co-Digestion, Methane Selectivity & Nutrient Optimization — RENVITEK
</h2>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Co-Digestion:
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Feedstock properties govern AD stability, biogas yield, and digestate quality. Heterogeneous 
wastes require optimized co-digestion to improve nutrient balance, pH stability, and methane 
yield. Optimal co-feed ratios, OLR, and HRT are feedstock-specific (e.g., fruit waste stabilized 
by protein-rich substrates). RENVITEK applies Design of Experiments (DoE)-based lab BMP 
and continuous trials, statistically evaluated using Pareto analysis, to identify high-impact 
feedstocks, optimal mixes, and support techno-economic decisions.</p>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Enhancing Methane Selectivity in Anaerobic Digestion:
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Reduced AD efficiency occurs when carbon shifts to CO₂ instead of CH₄ due to microbial imbalance. Methane selectivity is improved via methanogen enrichment, bioaugmentation, and optimization of pH, temperature, C/N ratio, and OLR. RENVITEK conducts controlled 
bioprocess studies to enhance biogas quality and CH₄ yield.
</p>
</div>
<div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Bioaugmentation:
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Targeted addition of hydrolytic, acidogenic, or methanogenic consortia/enzymes strengthens rate-limiting stages. RENVITEK performs microbial activity profiling (e.g., SMA tests), develops custom bioaugmentation protocols, and validates them via BMP and continuous reactor trials under mesophilic/thermophilic conditions to ensure scalable, cost-effective gains.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Nutrient Supplementation:
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Balanced macro- and micronutrients are essential across all AD stages. Fe, Ni, Co, Mo, Se act as enzyme cofactors, mitigate H₂S, VFAs, NH₃, and enable higher OLR. RENVITEK uses lab BMP and ≥3-month continuous trials (12 L reactors) to define deficiency thresholds, optimal dosages, and custom trace-element blends tailored to feedstock and operating conditions.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Food-to-Microorganism (F/M) Ratio Balance in Anaerobic Digestion
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">The Food-to-Microorganism (F/M) ratio is a key operational parameter governing the stability and performance of an anaerobic digester. It represents the balance between the amount of biodegradable organic substrate (“food”) and the active microbial biomass (“microorganisms”) available to degrade it. Maintaining an optimal F/M ratio is essential to ensure a RENVITEK state microbial ecosystem, efficient substrate conversion, and maximized biogas yield.</p><br></br>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Mathematically, the F/M ratio is expressed as:<div className="formula">
                <p className="font-mono text-emerald-700 dark:text-emerald-400 text-lg">
                  F/M = (VS<sub>feed</sub> × Q) / (V<sub>digester</sub> × X)
                </p>
              </div>
<ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">Where :
              <li>VS(feed) = Volatile solids in the feedstock (g/L)</li>
              <li>Q = Feed flow rate (L/day)</li>
              <li>V (digester) = Working volume of the digester (L)</li>
              <li>X = Volatile suspended solids (VSS) concentration (g/L) representing microbial biomass</li>
            </ul></p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Importance of F/M Ratio in AD Performance
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">A well-balanced F/M ratio ensures that microorganisms receive sufficient nutrients without 
overloading the system.</p><br></br>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">An imbalanced F/M ratio can lead to process instability and reduced methane production.
<ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">Where :
              <li><b>Low F/M ratio (&lt;0.2) →</b> Indicates excess microbial biomass relative to substrate. This can 
              lead to substrate limitation, energy starvation of methanogens, and reduced gas productivity.</li>
              <li><b>High F/M ratio (&gt;0.6) →</b> Results in substrate overloading, accumulation of <b>volatile fatty acids (VFAs),</b> pH drop, and process inhibition due to imbalance between acidogenic and methanogenic stages.</li>
              
            </ul>Maintaining the F/M ratio within an optimal range (typically <b>0.3–0.5 g VS/g VSS·day</b>) is therefore critical for <b>stable methane generation and digester health.</b></p>
          </div>
        </motion.div>
        </div>
      </section>

      {/* RENVITEK Services for F/M Ratio Optimization */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">RENVITEK Services for F/M Ratio Optimization</h2>
          
<div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Continuous Digestion Studies — RENVITEK
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK optimizes <b>F/M (Food-to-Microorganism)</b> ratio through <b>RBHM (RENVITEK Biodigester Health Monitoring)</b> by integrating real-time plant data with lab-scale validation.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Our F/M balance services include:
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>Feedstock Characterization: Determination of TS, VS, C/N ratio, BMP, and biodegradability 
index to quantify organic load.</li>
              <li>Microbial Biomass Estimation: Analysis of VSS, specific methanogenic activity (SMA), and 
microbial community dynamics using culture-based and molecular tools.</li>
              <li>F/M Ratio Modelling and Prediction: Using digester-specific kinetic and stoichiometric 
models to forecast system response under varied loading conditions.</li>
<li>Dynamic F/M Control Strategy: Development of feed modulation plans to stabilize AD 
performance during feedstock changes, load fluctuations, or process upsets.</li>
<li>On-site Advisory and Training: Providing real-time recommendations and operational 
adjustments for maintaining optimal microbial balance.</li>
            </ul>Through these integrated services, RENVITEK helps operators achieve consistent methane 
yields, stable pH, and improved digester resilience against overloading or substrate variability.</p>
            </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Laboratory Evaluation of F/M Balance
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">TRENVITEK also conducts controlled laboratory experiments simulating different F/M ratios under batch (BMP) and continuous digestion conditions. These tests provide quantitative insights into:</p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>Specific methane yield per unit VS added</li>
              <li>Rate of VFA accumulation and consumption</li>
              <li>Kinetic constants (k, µmax) under varying organic loads</li>
              <li>Microbial adaptation and recovery behaviour</li>
            </ul>Based on the experimental and on-site results, customized F/M management protocols are 
developed to maintain long-term digester health and prevent process instability.
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Optimized OLR & Inoculum Adaptation
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Organic Loading Rate (OLR) governs substrate conversion efficiency, microbial stability, and biogas productivity in AD. Excess or rapid OLR increases cause VFA accumulation, ammonia/H S inhibition, pH drop, and instability.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK applies a data-driven, stepwise OLR optimization via lab-scale continuous digestion (mono- and co-digestion), starting conservatively and increasing gradually while monitoring biogas yield, CH₄ %, VFAs, FOS/TAC, and pH. Performance deviations trigger targeted actions — nutrient supplementation, bioaugmentation, or feedstock optimization. If instability persists, the last stable value is set as the maximum sustainable OLR.</p>
<p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Trials under mesophilic/thermophilic conditions define safe operating limits before scale-up. Inoculum adaptation studies further ensure microbial acclimation to new/complex feedstocks through structured start-up and loading, delivering resilient, stable biogas performance under variable loads.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Feedstock Characterization
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK has characterized thousands of biomass samples (market/food waste, agricultural residues, industrial by-products) for AD and RNG applications. Our targeted analytical packages assess VS, C/N ratio, lignocellulosic fractions, macronutrients, and inhibitors to quantify biodegradability and methane potential, enabling optimized co-digestion ratios, stable operation, and higher biogas yields.</p>
          </div>
        </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-10 bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Publications on Anaerobic Digestion By The RENVITEK RENVITEK
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <motion.div
                className="bg-emerald-900/50 backdrop-blur p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-lg text-white/90 italic mb-6 leading-relaxed tracking-wider">
                  Srinivas Jukuri, Suresh Bastipati, Bhagawan Dheravath & Saida Lavudi(2021). Biochemical process evaluation of an anaerobic digester: Case studies on long sustain commercial biogas plant. Biomass Conversion and Biorefinery .{' '}
                  <a
                    href="https://doi.org/10.1007/s13399-021-01410-3"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 underline cursor-pointer hover:text-amber-300 break-all"
                  >
                  https://doi.org/10.1007/s13399-021-01410-3
                  </a>
                </p>
              </motion.div>
              <motion.div
                className="bg-emerald-900/50 backdrop-blur p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-lg text-white/90 italic mb-6 leading-relaxed tracking-wider">
                  Srinivas Jukuri, Saida Lavudi & Bhagawan Dheravath (2023). Enhancement of biogas
                  production from Market Yard Vegetable Waste using biological pre-hydrolysis—
                  a pilot scale. DOI:{' '}
                  <a
                    href="https://doi.org/10.1007/s13399-021-01410-3"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 underline cursor-pointer hover:text-amber-300 break-all"
                  >
                    https://doi.org/10.1007/s13399-021-01410-3
                  </a>
                  . Biomass Conversion and Biorefinery.
                </p>
              </motion.div>
              <motion.div
                className="bg-emerald-900/50 backdrop-blur p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-lg text-white/90 italic mb-6 leading-relaxed tracking-wider">
                 Optimizing Biogas production and mitigating scum layer formation in anaerobic digestion of market yard vegetable waste through varied Biological Pre-Hydrolysis. (Just accepted) Waste and Biomass Valorization.
                </p>
              </motion.div>
              <motion.div
                className="bg-emerald-900/50 backdrop-blur p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-lg text-white/90 italic mb-6 leading-relaxed tracking-wider">
                  Dileep Kumar Yeruva, Srinivas Jukuri, G. Velvizhi, A. Naresh Kumar, Y.V. Swamy, S.Venkata Mohan(2015). Integrating sequencing batch reactor with bio-electrochemical treatment  (BET) for augmenting remediation efficiency of complex petrochemical wastewater. {' '}
                  <a
                    href="https://doi:10.1016/j.biortech.2015.02.014"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 underline cursor-pointer hover:text-amber-300 break-all"
                  >
                    https://doi:10.1016/j.biortech.2015.02.014
                  </a>
                  Bioresource Technology.
                </p>
              </motion.div>
              <motion.div
                className="bg-emerald-900/50 backdrop-blur p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-lg text-white/90 italic mb-6 leading-relaxed tracking-wider">
                  K. Amulya, Srinivas Jukuri, S. Venkata Mohan(2015). Sustainable multistage process for enhanced productivity of bioplastics from waste remediation through aerobic dynamic feeding strategy: Process  integration for up-scaling. {' '}
                  <a
                    href="https://doi:10.1016/j.biortech.2015.01.070"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 underline cursor-pointer hover:text-amber-300 break-all"
                  >
                    https://doi:10.1016/j.biortech.2015.01.070
                  </a>
                  Bioresource Technology.
                </p>
              </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            Need Lab Analysis for Your Biogas Project?
          </h2>
          <p className="text-emerald-900 text-lg mb-8">
            Whether establishing a new biogas plant or managing multiple operational units, 
            RENVITEK's LAB supports clients in achieving sustainable energy goals.
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="lab-cta-btn"
            >
              Request Lab Services
              <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BiogasLabPage;
